import { useState } from "react";
import { Box, MenuItem, Select, Skeleton, Stack, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { FunctionField, useGetList } from "react-admin";
import { Link as RouterLink } from "react-router-dom";
import { PaginatedDatagrid } from "@/components/ui/react-admin/PaginatedDatagrid";
import { SectionCard } from "@/components/common/SectionCard";
import { TabContent } from "@/components/common/PageLayout";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { RAIL_COLLAPSE_QUERY } from "@/components/ui/react-admin/StyledShowWithRail";
import { ClaimItem, ResourceName } from "@/types";
import { ColorType, UIComponentSize } from "@/types/shared/uiComponents";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatLongDate } from "@/utils/formatters/dateFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { getStatusColor } from "@/utils/helpers/getStatusColor";
import { describeIneligibleReason } from "../claimItems/components/EligibilityBanner";
import { claimLinesLabel, useStoplossClaimContext } from "../hooks/useStoplossClaimContext";

type Eligibility = "all" | "eligible" | "ineligible";

/** Short chip text per ineligibility code; the long sentence stays in the tooltip. */
const SHORT_REASON: Record<string, string> = {
  // Codes the basis stamp writes (EligibilityBanner.INELIGIBLE_REASON_LABELS has the long form).
  below_deductible: "Below deductible",
  not_paid: "Not paid",
  missing_paid_date: "No paid date",
  missing_paid_amount: "No paid amount",
  missing_service_date: "No service date",
  paid_date_outside_policy: "Paid outside policy",
  service_date_outside_incurred_period: "Service outside incurred period",
  owned_by_other_term: "Belongs to another term",
  no_claimant: "No claimant",
  pending_recompute: "Recompute pending",
  not_in_deductible_basis: "Not in deductible basis",
};
export const shortIneligibleReason = (code: string): string =>
  SHORT_REASON[code] ?? formatToTitleCase(code.replace(/_/g, " "));

/**
 * Items stamped ineligible for these reasons still count on the claim they are
 * linked to: `owned_by_other_term` items are pulled in from a sibling term, and
 * `below_deductible` items accumulate toward it (basis_stamp.go; calc-expert
 * review 2026-10-23). Other reasons mean the item does not count.
 */
const COUNTS_DESPITE_STAMP = new Set(["owned_by_other_term", "below_deductible"]);
export const countsDespiteStamp = (reasons: string[]): boolean =>
  reasons.length > 0 && reasons.every((r) => COUNTS_DESPITE_STAMP.has(r));

const PER_PAGE = 25;

/**
 * Medical / Rx claims tab (#7584): which of the TPA's or PBM's claims on this
 * stop-loss claim the basis stamp marks eligible and why the rest are not.
 * Server-paged with the true count; the eligibility filter is the one the list
 * page sends (`is_eligible`). Columns fold at the rail breakpoints.
 *
 * Order: `GET /claim-items` ignores `sort` (claim_item_adapter.go orders by
 * date_of_service DESC, id), so the request and the caption say service date;
 * #7584 AC6's paid-date order waits on sort support in the API.
 *
 * Wording: these rows are "medical claims" / "Rx claims" in the UI (PO,
 * 2026-10-27); "claim item" is the storage term and stays in code and routes.
 */
export const StoplossClaimItemsTab = () => {
  const { claim, lineCounts } = useStoplossClaimContext();
  const [eligibility, setEligibility] = useState<Eligibility>("all");
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(PER_PAGE);
  const narrow = useMediaQuery("(max-width: 899px)");
  const collapsed = useMediaQuery(RAIL_COLLAPSE_QUERY);

  const filter: Record<string, unknown> = { stoploss_claim_id_ref: claim.id };
  if (eligibility === "eligible") filter.is_eligible = true;
  if (eligibility === "ineligible") filter.is_eligible = false;

  const {
    data: items = [],
    total,
    isPending,
  } = useGetList<ClaimItem>(ResourceName.CLAIM_ITEMS, {
    filter,
    pagination: { page: page + 1, perPage },
    sort: { field: "date_of_service", order: "DESC" },
  });

  const visibleTotal = total ?? items.length;
  const pagePaid = items
    .filter((l) => l.is_eligible !== false || countsDespiteStamp(l.ineligible_reasons ?? []))
    .reduce((s, l) => s + (typeof l.paid_amount === "number" ? l.paid_amount : 0), 0);

  const changeEligibility = (_: unknown, v: Eligibility | null) => {
    if (!v) return;
    setEligibility(v);
    setPage(0);
  };

  const count = (n?: number) => (n === undefined ? "" : ` ${n}`);
  const noun = claimLinesLabel(claim);
  const nounLower = noun.replace(/^Medical/, "medical");
  const procedure = (l: ClaimItem) => [l.procedure_code, l.procedure_description].filter(Boolean).join(" ");
  const sourceId = (l: ClaimItem) => (l.source_documents?.[0] ? String(l.source_documents[0]) : undefined);
  const muted = (l: ClaimItem) => l.is_eligible === false && !countsDespiteStamp(l.ineligible_reasons ?? []);

  const toolbar = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.25,
        flexWrap: "wrap",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* A quiet view picker: a table filter should read as a setting, not a control that wants attention. */}
      <Select
        size="small"
        value={eligibility}
        onChange={(e) => changeEligibility(null, e.target.value as Eligibility)}
        inputProps={{ "aria-label": "Which claims to show" }}
        data-testid="lines-eligibility"
        variant="standard"
        disableUnderline
        sx={{
          fontSize: "0.8125rem",
          fontWeight: 600,
          "& .MuiSelect-select": { py: 0.5, pr: "28px !important" },
        }}
      >
        <MenuItem value="all">All claims{count(lineCounts.all)}</MenuItem>
        <MenuItem value="eligible">Eligible{count(lineCounts.eligible)}</MenuItem>
        <MenuItem value="ineligible">Not eligible{count(lineCounts.ineligible)}</MenuItem>
      </Select>
      <Typography variant="caption" color="text.secondary">
        Service date, newest first
      </Typography>
    </Box>
  );

  return (
    <TabContent>
      <SectionCard compact noPadding noDivider>
        {toolbar}
        {isPending ? (
          <Stack spacing={1} sx={{ p: 2 }} data-testid="lines-skeleton">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rounded" height={36} />
            ))}
          </Stack>
        ) : items.length === 0 ? (
          <Box sx={{ py: 5, px: 2, textAlign: "center" }} data-testid="lines-empty">
            <Typography variant="body2" color="text.secondary">
              {eligibility === "all"
                ? `No ${nounLower} yet.`
                : `No ${eligibility === "ineligible" ? "ineligible" : "eligible"} claims.`}
            </Typography>
            {eligibility === "all" ? (
              <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                They arrive when a TPA or PBM claims file loads, or when you add one.
              </Typography>
            ) : null}
          </Box>
        ) : (
          <>
            {/* The card clips overflow, so the table scrolls sideways inside it when the column is narrow. */}
            <Box sx={{ overflowX: "auto", "& table": { minWidth: narrow ? 560 : 760 } }}>
              <PaginatedDatagrid
                data={items}
                serverSide
                totalCount={visibleTotal}
                page={page}
                onPageChange={setPage}
                rowsPerPage={perPage}
                onRowsPerPageChange={(n) => {
                  setPerPage(n);
                  setPage(0);
                }}
                rowClick={(id) => `/claim-items/${id}/show`}
                resource={ResourceName.CLAIM_ITEMS}
              >
                <FunctionField
                  label="Service date"
                  render={(l: ClaimItem) => (
                    <Box>
                      <Typography variant="body2" color={muted(l) ? "text.secondary" : "text.primary"}>
                        {formatLongDate(l.date_of_service)}
                      </Typography>
                      {narrow ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {l.paid_date ? `paid ${formatLongDate(l.paid_date)}` : "no paid date"}
                          {collapsed && (l.provider || l.procedure_code)
                            ? `, ${[l.provider, l.procedure_code].filter(Boolean).join(", ")}`
                            : ""}
                        </Typography>
                      ) : null}
                    </Box>
                  )}
                />
                {!narrow ? (
                  <FunctionField
                    label="Paid date"
                    render={(l: ClaimItem) => (
                      <Typography variant="body2" color="text.secondary">
                        {l.paid_date ? formatLongDate(l.paid_date) : "—"}
                      </Typography>
                    )}
                  />
                ) : null}
                {!collapsed ? (
                  <FunctionField
                    label="Provider"
                    render={(l: ClaimItem) => (
                      <Box>
                        <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 260 }}>
                          {l.provider || "—"}
                        </Typography>
                        {narrow && procedure(l) ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            noWrap
                            sx={{ maxWidth: 260 }}
                          >
                            {procedure(l)}
                          </Typography>
                        ) : null}
                      </Box>
                    )}
                  />
                ) : null}
                {!narrow ? (
                  <FunctionField
                    label="Procedure"
                    render={(l: ClaimItem) => (
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 240 }}>
                        {procedure(l) || "—"}
                      </Typography>
                    )}
                  />
                ) : null}
                <FunctionField
                  label="Status"
                  render={(l: ClaimItem) => {
                    const excluded = l.is_eligible === false;
                    const reasons = l.ineligible_reasons ?? [];
                    const countsAnyway = excluded && countsDespiteStamp(reasons);
                    if (!excluded) {
                      return (
                        <StatusChip
                          label={formatToTitleCase(l.claim_status || "—")}
                          color={getStatusColor(l.claim_status) ?? ColorType.Default}
                          size={UIComponentSize.Small}
                        />
                      );
                    }
                    const tooltip = reasons.map(describeIneligibleReason).join(" ");
                    return (
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
                        <Tooltip title={collapsed ? tooltip : ""}>
                          <span>
                            <StatusChip
                              label={countsAnyway ? "Counts" : "Not eligible"}
                              color={countsAnyway ? "info" : "muted"}
                              size={UIComponentSize.Small}
                            />
                          </span>
                        </Tooltip>
                        {!collapsed &&
                          reasons.map((code) => (
                            <Tooltip key={code} title={describeIneligibleReason(code)}>
                              <span>
                                <StatusChip
                                  label={shortIneligibleReason(code)}
                                  color={ColorType.Default}
                                  size={UIComponentSize.Small}
                                />
                              </span>
                            </Tooltip>
                          ))}
                      </Box>
                    );
                  }}
                />
                <FunctionField
                  label="Paid"
                  textAlign="right"
                  render={(l: ClaimItem) => (
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{
                        textAlign: "right",
                        color: muted(l) ? "text.secondary" : "text.primary",
                        textDecoration: muted(l) ? "line-through" : "none",
                      }}
                    >
                      {typeof l.paid_amount === "number" ? formatUSD(l.paid_amount) : "—"}
                    </Typography>
                  )}
                />
                {!collapsed ? (
                  <FunctionField
                    label="Source"
                    render={(l: ClaimItem) => {
                      const id = sourceId(l);
                      return id ? (
                        <Typography
                          component={RouterLink}
                          to={`/documents/${id}/show`}
                          variant="caption"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ color: "inherit", textDecoration: "underline", "&:hover": { color: "primary.main" } }}
                        >
                          Document
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      );
                    }}
                  />
                ) : null}
              </PaginatedDatagrid>
            </Box>

            <Box
              data-testid="lines-footer"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                px: 2,
                py: 1.25,
                borderTop: "1px solid",
                borderColor: "divider",
                flexWrap: "wrap",
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {lineCounts.eligible ?? "—"} eligible, {lineCounts.ineligible ?? "—"} not eligible
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Paid on this page {formatUSD(pagePaid)} (not the stop-loss claim amount)
              </Typography>
            </Box>
          </>
        )}
      </SectionCard>
    </TabContent>
  );
};
